import { Ban, CheckCircle, Clock, XCircle } from "lucide-react";
import { PaymentDetails } from "../components/Transactions/types/Exchange";
import { BLOCKCHAIN_NETWORK, BLOCKED_EMAIL_DOMAINS, currencyPaymentMethodMap, EMAIL_PAYMENT_METHODS, INTERNATIONAL_BANK_ISO2, isBankTransferMethod, MOMO_COUNTRIES_ISO2, ORDER_STATUS, PAYMENT_METHOD, PAYMENTLINK_STATUS, UNIFIED_STATUS, WEBHOOK_EVENT_TYPE_MAP, WEBHOOK_STATUS } from "./constant";
import { OrderStatus } from "../components/Transactions/Order";
import { PayLinkStatus } from "../components/PaymentLink/types";
import { useOrdersStore } from "../stores/useOrderStore";
import { stableCurrencies } from "./countries";
import { Webhook } from "../stores/useWebhookStore";
import { ValidatePinChangeParams } from "./type";

export const getBlockchainName = (value?: string | BLOCKCHAIN_NETWORK | null) => {
  if (!value) return null;

  switch (value) {
    case BLOCKCHAIN_NETWORK.TRC20:
      return "Tron (TRC20)";
    case BLOCKCHAIN_NETWORK.ERC20:
      return "Ethereum (ERC20)";
    case BLOCKCHAIN_NETWORK.BEP20:
      return "BNB Smart Chain (BEP20)";
    case BLOCKCHAIN_NETWORK.SOL:
      return "Solana (SOL)";
    case BLOCKCHAIN_NETWORK.TON:
      return "The Open Network (TON)";
    case BLOCKCHAIN_NETWORK.BASE:
      return "BASE";
    case BLOCKCHAIN_NETWORK.POL:
      return "Polygon (POL)";
    case BLOCKCHAIN_NETWORK.ARB:
      return "Arbitrum (ARB)";
    default:
      return null;
  }
};

export const validatePaymentDetails = (
  paymentDetails: { debit: PaymentDetails; credit: PaymentDetails },
  side: "debit" | "credit"
): string | null => {
  const details = paymentDetails[side];
  if (!details) return `${side} details are missing`;

  if (EMAIL_PAYMENT_METHODS.has(details.paymentMethod)) {
    if (!details.email) return `${side} email is required`;
    return null;
  }

  switch (details.paymentMethod) {
    // case PAYMENT_METHOD.WALLET:

    //   break;

    case PAYMENT_METHOD.MOBILE_MONEY:
      if (!details.accountNumber) return `${side} account number is required`;
      if (!details.providerName) return `${side} provider network is required`;
      break;

    case PAYMENT_METHOD.STABLE_CURRENCY:
      if (!details.walletAddress) return `${side} wallet address is required`;
      if (!details.network) return `${side} blockchain is required`;
      break;

    case PAYMENT_METHOD.BANK_TRANSFER:
    case PAYMENT_METHOD.ACH:
    case PAYMENT_METHOD.WIRE:
      if (!details.accountNumber) return `${side} account number is required`;
      if (!details.accountName) return `${side} bank name is required`;
      break;

    case PAYMENT_METHOD.WECHAT_PAY:
    case PAYMENT_METHOD.ALIPAY:
      if (!details.phoneNumber) return `${side} Phone Number is required`;
      break;

    default:
      if (!details.accountNumber) return `${side} account number is required`;
  }

  return null;
};

export const normalizeToUsd = (currency: string) =>
         ["USDT", "USDC", "BUSD"].includes(currency) ? "USD" : currency;

export const getNetworkCode = (
  network?: string | { code: string }
): string | undefined => {
  if (!network) return undefined;
  return typeof network === "string" ? network : network.code;
};

export const buildApiPayload = (raw: any) => {
  let apiPayload: any;

  switch (raw.paymentMethod) {
    case PAYMENT_METHOD.MOBILE_MONEY:
      apiPayload = {
        accountName: raw.accountName,
        accountNumber: raw.accountNumber,
        providerName: raw.providerName,
        paymentMethod: raw.paymentMethod,
        iso2: raw.iso2,
        currency: raw.currency,
        network: raw.providerName, // keep network explicitly
      };
      break;

    case PAYMENT_METHOD.BANK_TRANSFER:
    case PAYMENT_METHOD.ACH:
    case PAYMENT_METHOD.WIRE:
      if (INTERNATIONAL_BANK_ISO2.has(raw.iso2)) {
        // International bank transfer
        apiPayload = {
          ...raw,
          providerName: raw.providerName?.trim() ? raw.providerName : raw.bankCode,
        };
        delete apiPayload.bankCodeLabel;
        delete apiPayload.walletAddress;
        delete apiPayload.email;
        delete apiPayload.network;
      } else if (MOMO_COUNTRIES_ISO2.has(raw.iso2)) {
        // Bank transfer in MoMo-enabled countries
        apiPayload = {
          ...raw,
          bankCode: raw.providerName,
        };
        delete apiPayload.network;
        delete apiPayload.email;
        delete apiPayload.walletAddress;
      } else {
        // Local bank transfer (default bank transfer case)
        apiPayload = { 
          ...raw,
          bankCode: raw.bankCode || raw.providerName,
         };
        delete apiPayload.walletAddress;
        delete apiPayload.email;
        delete apiPayload.network;
      }
      break;

    case PAYMENT_METHOD.STABLE_CURRENCY:
      apiPayload = { ...raw };
      delete apiPayload.bankCodeLabel;
      delete apiPayload.bankCode;
      delete apiPayload.providerName;
      delete apiPayload.accountNumber;
      delete apiPayload.accountName;
      delete apiPayload.email;
      break;


      case PAYMENT_METHOD.WECHAT_PAY:
case PAYMENT_METHOD.ALIPAY:
  apiPayload = {
    phoneNumber: raw.phoneNumber,
    accountName: raw.accountName,
    paymentMethod: raw.paymentMethod,
    iso2: raw.iso2,
    currency: raw.currency,
  };
  break;

    case PAYMENT_METHOD.ZELLE:
    case PAYMENT_METHOD.INTERAC:
      // Email-like methods
      apiPayload = { ...raw };
      delete apiPayload.bankCode;
      delete apiPayload.network;
      delete apiPayload.providerName;
      delete apiPayload.accountNumber;
      delete apiPayload.walletAddress;
      break;

    case PAYMENT_METHOD.WALLET:
    default:
      apiPayload = { ...raw };
      break;
  }

  return apiPayload;
};

export const isBusinessEmail = (email: string): boolean => {
  if (!email.includes("@")) return false;

  const domain = email.split("@")[1].toLowerCase();

  if (BLOCKED_EMAIL_DOMAINS.has(domain)) return false;

  const parts = domain.split(".");
  if (parts.length > 2) {
    const rootDomain = parts.slice(-2).join(".");
    if (BLOCKED_EMAIL_DOMAINS.has(rootDomain)) return false;
  }

  return true;
};

export const getStatusConfig = (status: any, isExpired: any) => {
  const configs : any = {
    PAID: {
      bg: "bg-[#E2F0E8] dark:bg-[#1E1E1E]/20 border dark:border-green-800",
      icon: CheckCircle,
      iconColor: "text-green-600 dark:text-green-400",
      title: "Payment Successful!",
      message: "Your payment has been completed successfully.",
      titleColor: "text-black dark:text-white",
      messageColor: "text-gray-700 dark:text-gray-400"
    },
    DECLINED: {
      bg: "bg-[#F0E2E2] dark:bg-[#1E1E1E]/20 border dark:border-red-800",
      icon: XCircle,
      iconColor: "text-red-600 dark:text-red-400",
      title: "Payment Declined",
      message: "This payment request has been declined.",
      titleColor: "text-black dark:text-white",
      messageColor: "text-gray-700 dark:text-gray-400"
    },
    CANCELLED: {
      bg: "bg-[#F0F4F8]  dark:bg-[#1E1E1E]/20 border dark:border-[#F0F4F8]",
      icon: Ban,
      iconColor: "text-orange-600 dark:text-orange-400",
      title: "Payment Cancelled", 
      message: "This payment has been cancelled by the creator.",
      titleColor: "text-black dark:text-white",
      messageColor: "text-gray-700 dark:text-gray-400"
    },
    EXPIRED: {
      bg: "bg-gray-100 dark:bg-[#1E1E1E]/20 border dark:border-[#F0F4F8]",
      icon: Clock,
      iconColor: "text-gray-600 dark:text-gray-400",
      title: "Payment Expired",
      message: "This payment link is no longer valid.",
      titleColor: "text-black dark:text-white",
      messageColor: "text-gray-700 dark:text-gray-400"
    }
  };

  if (isExpired) return configs.EXPIRED;
  return configs[status]  || configs.EXPIRED;
};

export const getOrderStatusBadgeColor = (status: OrderStatus) => {
  switch (status) {
    case ORDER_STATUS.PROCESSING:
      return "warning";
    case ORDER_STATUS.SUCCESSFUL:
      return "success";
    case ORDER_STATUS.PENDING:
      return "info";
    case ORDER_STATUS.CANCELLED:
    default:
      return "error";
  }
};

export const getPaymentlinkStatusBadgeColor = (status: PayLinkStatus) => {
  switch (status) {
    case PAYMENTLINK_STATUS.PAID:
      return "success";
    case PAYMENTLINK_STATUS.PENDING:
      return "info";
    case PAYMENTLINK_STATUS.PROCESSING:
      return "warning";
    case PAYMENTLINK_STATUS.FAILED:
      return "error";
    case PAYMENTLINK_STATUS.CANCELLED:
      return "dark";
    case PAYMENTLINK_STATUS.EXPIRED:
      return "light";
    case PAYMENTLINK_STATUS.DECLINED:
      return "error";
    default:
      return "info";
  }
};

export const getWebhookStatusBadgeColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case WEBHOOK_STATUS.SUCCESSFUL:
      return 'success';
    case WEBHOOK_STATUS.FAILED:
      return 'error';
    default:
      return 'info';
  }
};

export const toFixed = (numberValue: number, places = 2) => {
  const [beforeDecimal, afterDecimal] = String(numberValue).split(".");
  if (!afterDecimal) return numberValue;

  return Number(`${beforeDecimal}.${afterDecimal.substring(0, places)}`);
};

export const waitForLoadingToComplete = async (maxAttempts = 30, delayMs = 400) => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const { loading } = useOrdersStore.getState();
    
    if (!loading) {
      return;
    }
    
    await new Promise(resolve => setTimeout(resolve, delayMs));
  }
  
  return;
};

export const FormatNumber = (number: string | number) => {
  const parts = number.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

export const to2DecimalPlace = (number: any) => {
  number = String(number);
  const decimalPosition = number.indexOf(".");
  if (decimalPosition > -1) {
    let digitAfterDecimal = number.substring(
      decimalPosition + 1,
      decimalPosition + 3
    );

    if (Number(digitAfterDecimal || 0) >= 98)
      number = Number(number).toFixed(2);
    else number = number.substring(0, decimalPosition + 3);
  }
  return Number(number);
};


export const getWebhookTransactionData = (webhook: Webhook) => {
  const data = webhook.data;
  
  // 1. Prioritize eventType if available (New implementation)
  if ((webhook as any).eventType) {
    const category = WEBHOOK_EVENT_TYPE_MAP[(webhook as any).eventType] || 'unknown';
    return {
      ...data,
      category
    };
  }
  
  /**
   * Fall back logic to determine the category of the webhook data based on its properties.
   */
  // ---- Check if it's a Payment Link
  if (data.paymentDetails && data.currencyIso2 && data.payerDetails) {
    return {
      ...data,
      category: 'payment_link' as const
    };
  }
  
  // ---- Check if it's an Exchange
  if (data.fromCurrency && data.toCurrency && data.rate !== undefined) {
    return {
      ...data,
      category: 'exchange' as const
    };
  }
 
    // ---- Check if it's a Wallet Creation
  // Use !== undefined checks because balance/heldBalance can be 0 (falsy)
  if (data.id && data.currency && data.balance !== undefined && data.heldBalance !== undefined && !data.reference) {
    return { ...data, category: 'wallet_creation' as const };
  }

  // ---- Check if it's a Wallet Transaction
  if (data.id && data.reference && data.purpose !== undefined) {
    return { ...data, category: 'wallet_txn' as const };
  }

  // ---- Check if it's a stable wallet creation
  if (data.userId && data.address && data.type) {
    return { ...data, category: 'stable_wallet_creation' as const };
  }

  //  ---- Check if its a stable wallet transaction (stable currency wallet transaction)
  if (data.id && data.token && data.network && data.from && data.to && data.amount !== undefined) {
    return { ...data, category: 'stable_wallet_txn' as const };
  }

  // ---- Check if it's a Wallet Virtual Account
  if (data.accountName && data.accountNumber && data.bankCode && data.bankName && data.accountRef) {
    return { ...data, category: 'wallet_virtual_account' as const };
  }

  
  // ---- Fallback to original data with unknown type
  return {
    ...data,
    category: 'unknown' as const
  };
};

export const getUnifiedStatusBadgeColor = (status: string) => {
  switch (status) {
    case UNIFIED_STATUS.PAID:
    case UNIFIED_STATUS.SUCCESSFUL:
      return "success";

    case UNIFIED_STATUS.PENDING:
      return "info";

    case UNIFIED_STATUS.PROCESSING:
      return "warning";

    case UNIFIED_STATUS.FAILED:
    case UNIFIED_STATUS.DECLINED:
      return "error";

    case UNIFIED_STATUS.CANCELLED:
      return "dark";

    case UNIFIED_STATUS.EXPIRED:
      return "light";

    default:
      return "info";
  }
};

const currencySymbolMap = stableCurrencies.reduce((acc, country) => {
  acc[country.currencyCode] = country.currencySymbol;
  return acc;
}, {} as { [key: string]: string });

export const getCurrencySymbol = (currencyCode: string) => {
  return currencySymbolMap[currencyCode] ?? null;
};

export const resolvePaymentMethod = (
  currencyCode: string,
  paymentMethod: { bankTransfer: boolean; mobileMoney: boolean }
): "BANK_TRANSFER" | "MOBILE_MONEY" => {
  if (currencyPaymentMethodMap[currencyCode]) {
    return currencyPaymentMethodMap[currencyCode];
  }

  const { bankTransfer, mobileMoney } = paymentMethod;

  if (mobileMoney && !bankTransfer) return "MOBILE_MONEY";
  if (bankTransfer && !mobileMoney) return "BANK_TRANSFER";

  return "BANK_TRANSFER";
};

export const buildPaymentDetails = (details: any) => {
  const baseDetails = {
    accountNumber: details.accountNumber,
    accountName: details.accountName,
    iso2: details.iso2,
    currency: details.currency,
  };

  if (details.paymentMethod === PAYMENT_METHOD.WALLET) {
    return {
      ...baseDetails,
      paymentMethod: PAYMENT_METHOD.WALLET,
      walletId: details.accountNumber,
    };
  }

  if (EMAIL_PAYMENT_METHODS.has(details.paymentMethod)) {
    return {
      accountName: details.accountName,
      iso2: details.iso2,
      currency: details.currency,
      paymentMethod: details.paymentMethod,
      email: details.email,
    };
  }

  if (
  details.paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
  details.paymentMethod === PAYMENT_METHOD.ALIPAY
) {
  return {
    phoneNumber: details.phoneNumber,
    accountName: details.accountName,
    iso2: details.iso2,
    currency: details.currency,
    paymentMethod: details.paymentMethod,
  };
}

  if (details.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
    return {
      currency: details.currency,
      iso2: details.iso2,
      paymentMethod: details.paymentMethod,
      walletAddress: details.walletAddress,
      network: details.network,
    };
  }

  if (details.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
    return {
      ...baseDetails,
      network: details.providerName,
      paymentMethod: details.paymentMethod,
      providerName: details.providerName,
    };
  }

  if (isBankTransferMethod(details.paymentMethod)) {
    const bankTransferDetails: any = {
      ...baseDetails,
      bankCode: details.bankCode || "",
      paymentMethod: details.paymentMethod || "",
      providerName: details.bankCode || "",
    };

    if (details.iso2 === "NG") {
      bankTransferDetails.providerName = details.providerName;
    } else if (details?.iso2 && INTERNATIONAL_BANK_ISO2.has(details.iso2)) {
      bankTransferDetails.bankCode = details.bankCode || "";
      bankTransferDetails.providerName = details.providerName?.trim() ? details.providerName : details.bankCode || "";
    } else if (details?.iso2 && MOMO_COUNTRIES_ISO2.has(details.iso2)) {
      bankTransferDetails.bankCode = details.providerName || "";
      bankTransferDetails.providerName = details.providerName || "";
    } else {
      bankTransferDetails.bankCode = details.bankCode || "";
      bankTransferDetails.providerName =
        details.providerName || details.bankCode || "";
    }
    if (details.iso2 === "GB" || details.currency === "EUR") {
    bankTransferDetails.holderCity = details.holderCity || "";
    bankTransferDetails.holderStreet = details.holderStreet || "";
    bankTransferDetails.holderPostalCode = details.holderPostalCode || "";
    bankTransferDetails.providerName = details.providerName || details.bankCode || "";
  }

    return bankTransferDetails;
  }

  return baseDetails;
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const autoClearError = (
  setter: React.Dispatch<React.SetStateAction<string>>,
  delay = 3000
) => {
  setTimeout(() => setter(""), delay);
};

export const validatePinChange = ({
  oldPin,
  pin,
  confirmPin,
}: ValidatePinChangeParams): string | null => {
  if (!oldPin || !pin || !confirmPin) {
    return "All fields are required.";
  }

  if (
    oldPin.length !== 4 ||
    pin.length !== 4 ||
    confirmPin.length !== 4
  ) {
    return "PIN must be exactly 4 digits long.";
  }

  const pinRegex = /^\d{4}$/;
  if (
    !pinRegex.test(oldPin) ||
    !pinRegex.test(pin) ||
    !pinRegex.test(confirmPin)
  ) {
    return "PIN must contain only numbers.";
  }

  if (pin !== confirmPin) {
    return "New PIN and Confirm PIN do not match.";
  }

  if (oldPin === pin) {
    return "New PIN must be different from Old PIN.";
  }

  return null; // ✅ valid
};

export const validateCreatePin = ({pin, confirmPin}: {pin: string, confirmPin: string}): string | null => {
  if (!pin || !confirmPin) {
    return "Both PIN fields are required.";
  }

  if (pin.length !== 4 || confirmPin.length !== 4) {
    return "PIN must be exactly 4 digits long.";
  }

  const pinRegex = /^\d{4}$/;
  if (!pinRegex.test(pin) || !pinRegex.test(confirmPin)) {
    return "PIN must contain only numbers.";
  }

  if (pin !== confirmPin) {
    return "PIN and Confirm PIN do not match.";
  }

  return null;
}
